/**
 * chatRuntime — app-level factory plus the session-scoped singleton accessor.
 *
 * **There is exactly ONE runtime per signed-in session, and it outlives every token
 * refresh.** This used to key a map by the WebPubSub URL, on the premise that the URL
 * changes only on sign-out. It does not: `doUpdateToken` dispatches `authSignIn` on
 * every Keycloak refresh and `/session/update` returns fresh `ws_info`, so the URL
 * rotates on the refresh interval.
 */

import { store } from 'src/redux/store';

import { CommandBus } from './commands';
import {
  analyticsMiddleware,
  errorMappingMiddleware,
  tokenRefreshMiddleware
} from './commands';
import { MachineRegistry } from './machines';
import { ChatStreamRegistry, StreamRunner, StreamSettler } from './stream';
import { createAzureWebPubSubAdapter } from './transport/AzureWebPubSubAdapter';
import type { ChatTransport } from './transport/ChatTransport';
import type { ChatId } from './types/ids';

export interface ChatRuntime {
  transport: ChatTransport;
  streamRegistry: ChatStreamRegistry;
  machineRegistry: MachineRegistry;
  streamRunner: StreamRunner;
  streamSettler: StreamSettler;
  commandBus: CommandBus;
}

export function createChatRuntime(transport: ChatTransport): ChatRuntime {
  const streamRegistry = new ChatStreamRegistry();
  const machineRegistry = new MachineRegistry(streamRegistry);
  const streamRunner = new StreamRunner(
    transport,
    machineRegistry,
    streamRegistry
  );
  const streamSettler = new StreamSettler({
    machineRegistry,
    streamRegistry,
    transport,
    dispatch: store.dispatch
  });

  const commandBus = new CommandBus({
    machineRegistry,
    streamRegistry,
    streamRunner,
    streamSettler,
    transport
  });
  commandBus
    .use(analyticsMiddleware)
    .use(errorMappingMiddleware)
    .use(tokenRefreshMiddleware);

  return {
    transport,
    streamRegistry,
    machineRegistry,
    streamRunner,
    streamSettler,
    commandBus
  };
}

let _runtime: ChatRuntime | undefined;

function currentWsUrl(): string {
  const url = store.getState().auth.azureWebPubSubInfo?.url;
  if (!url) {
    throw new Error('No WebPubSub URL available — session not established');
  }
  return url;
}

export function getSharedRuntime(): ChatRuntime {
  if (!_runtime) {
    const transport = createAzureWebPubSubAdapter({ getUrl: currentWsUrl });
    _runtime = createChatRuntime(transport);
    void transport.start();
  }
  return _runtime;
}

/**
 * The runtime for `wsUrl` IF one already exists — never creates.
 */
export function peekSharedRuntime(): ChatRuntime | undefined {
  return _runtime;
}

/** Test / sign-out escape hatch. Stops the transport and clears the singleton. */
export async function releaseSharedRuntime(): Promise<void> {
  const rt = _runtime;
  if (!rt) {
    return;
  }
  _runtime = undefined;
  await rt.transport.stop();
  _sessionKeyByServerId.clear();
}

const _sessionKeyByServerId = new Map<string, ChatId>();

/**
 * Record that `serverId` belongs to an already-running `sessionKey`.
 */
export function rememberSessionKey(
  serverId: string | number,
  sessionKey: ChatId
): void {
  _sessionKeyByServerId.set(String(serverId), sessionKey);
}

/**
 * The session key a server id is already being streamed under, if any.
 */
export function resolveSessionKey(
  serverId: string | number | undefined
): ChatId | undefined {
  return serverId == null
    ? undefined
    : _sessionKeyByServerId.get(String(serverId));
}
